export interface CountryOption {
  code: string;
  name: string;
}

export interface LanguageOption {
  code: string;
  label: string;
  badge: string;
  countries: string[];
}

export interface ChoiceOption {
  label: string;
  icon: string;
}

export interface VoiceOption {
  id: string;
  label: string;
  gender: 'female' | 'male';
}

export interface VoiceStyleOption {
  label: string;
  prompt: string;
}

export const COUNTRIES: CountryOption[] = [
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'SA', name: 'Saudi Arabia' },
];

export const LANGUAGES: LanguageOption[] = [
  { code: 'en-US', label: 'English', badge: 'us', countries: ['US', 'CA', 'AU', 'SG'] },
  { code: 'en-GB', label: 'English', badge: 'gb', countries: ['GB'] },
  { code: 'en-IN', label: 'English', badge: 'in', countries: ['IN', 'AE', 'SG'] },
  { code: 'hi-IN', label: 'Hindi', badge: 'in', countries: ['IN'] },
  { code: 'ar-AE', label: 'Arabic', badge: 'ae', countries: ['AE', 'SA'] },
  { code: 'es-ES', label: 'Spanish', badge: 'es', countries: ['ES', 'US'] },
  { code: 'fr-FR', label: 'French', badge: 'fr', countries: ['FR', 'CA'] },
  { code: 'de-DE', label: 'German', badge: 'de', countries: ['DE'] },
];

export const TTS_PROVIDERS = ['google-cloud'];

export const TTS_MODELS = ['standard'];

export const TTS_LANGUAGES = ['en-IN', 'en-US', 'en-GB', 'hi-IN', 'ar-AE', 'es-ES', 'fr-FR', 'de-DE'];

export const VOICES: VoiceOption[] = [
  { id: 'en-IN-Standard-B', label: 'en-IN-Standard-B', gender: 'female' },
  { id: 'en-IN-Standard-A', label: 'en-IN-Standard-A', gender: 'male' },
];

export const VOICE_STYLES: VoiceStyleOption[] = [
  {
    label: 'Friendly – Warm & approachable',
    prompt: 'Speak in a friendly, warm, approachable tone.',
  },
  {
    label: 'Professional – Clear & confident',
    prompt: 'Speak in a professional, clear, confident tone.',
  },
  {
    label: 'Calm – Patient & reassuring',
    prompt: 'Speak in a calm, patient, reassuring tone.',
  },
  {
    label: 'Energetic – Upbeat & engaging',
    prompt: 'Speak in an energetic, upbeat, engaging tone.',
  },
];

export const TASKS: ChoiceOption[] = [
  { label: 'Customer Support', icon: '🎧' },
  { label: 'Sales & Marketing', icon: '💼' },
  { label: 'Appointment Scheduling', icon: '📅' },
  { label: 'Lead Qualification', icon: '🎯' },
  { label: 'Billing Support', icon: '💳' },
  { label: 'Receptionist', icon: '🛎️' },
  { label: 'Technical Support', icon: '🛠️' },
  { label: 'Order Support', icon: '📦' },
  { label: 'Patient Coordination', icon: '🏥' },
  { label: 'Student Assistance', icon: '🎓' },
  { label: 'Feedback & Surveys', icon: '💬' },
  { label: 'General Assistant', icon: '✨' },
];

export const INDUSTRIES: ChoiceOption[] = [
  { label: 'Real Estate', icon: '🏠' },
  { label: 'Healthcare', icon: '🏥' },
  { label: 'Dental', icon: '🦷' },
  { label: 'Fitness & Wellness', icon: '💪' },
  { label: 'Education', icon: '🎓' },
  { label: 'Finance & Banking', icon: '🏦' },
  { label: 'E-commerce', icon: '🛒' },
  { label: 'Hospitality', icon: '🏨' },
  { label: 'Legal', icon: '⚖️' },
  { label: 'Insurance', icon: '🛡️' },
  { label: 'Automotive', icon: '🚗' },
  { label: 'Travel & Tourism', icon: '✈️' },
];

export const NICHES: Record<string, string[]> = {
  'Real Estate': [
    'Residential Sales',
    'Commercial Leasing',
    'Property Management',
    'Rentals',
    'Mortgage Advisory',
    'New Developments',
  ],
  Healthcare: [
    'Hospitals',
    'Clinics',
    'Telemedicine',
    'Diagnostics',
    'Pharmacy',
    'Home Healthcare',
  ],
  Dental: [
    'General Dentistry',
    'Orthodontics',
    'Cosmetic Dentistry',
    'Pediatric Dentistry',
    'Oral Surgery',
    'Dental Clinics',
  ],
  'Fitness & Wellness': [
    'Gyms',
    'Personal Training',
    'Yoga & Pilates',
    'Nutrition Coaching',
    'Wellness Spas',
    'Sports Clubs',
  ],
  Education: [
    'K-12 Schools',
    'Higher Education',
    'Online Learning Platforms',
    'Tutoring Services',
    'Vocational Training',
    'Language Schools',
    'Test Preparation',
    'Corporate Training',
    'Coaching Institutes',
    'Study Abroad',
    'Special Education',
    'EdTech Products',
  ],
  'Finance & Banking': [
    'Retail Banking',
    'Lending',
    'Wealth Management',
    'Payments',
    'Accounting Firms',
    'Credit Unions',
  ],
  'E-commerce': [
    'Fashion',
    'Electronics',
    'Grocery',
    'Marketplace',
    'D2C Brands',
    'Subscription Boxes',
  ],
  Hospitality: [
    'Hotels',
    'Restaurants',
    'Cafes',
    'Event Venues',
    'Resorts',
    'Catering',
  ],
  Legal: [
    'Law Firms',
    'Legal Aid',
    'Corporate Law',
    'Family Law',
    'Immigration',
    'Compliance',
  ],
  Insurance: [
    'Health Insurance',
    'Life Insurance',
    'Motor Insurance',
    'Property Insurance',
    'Claims Support',
    'Brokers',
  ],
  Automotive: [
    'Dealerships',
    'Service Centers',
    'Spare Parts',
    'Car Rentals',
    'EV Sales',
    'Fleet Management',
  ],
  'Travel & Tourism': [
    'Travel Agencies',
    'Tour Operators',
    'Airlines',
    'Hotels & Stays',
    'Visa Assistance',
    'Holiday Packages',
  ],
};

export const languagesForCountries = (countries: string[]): LanguageOption[] => {
  if (!countries.length) return LANGUAGES;
  return LANGUAGES.filter((language) => language.countries.some((code) => countries.includes(code)));
};

export const countryName = (code: string): string =>
  COUNTRIES.find((country) => country.code === code)?.name || code;

export const withCurrentOption = (options: string[], current: string): string[] => {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
};
